// Utility to handle multipart uploads to S3 using pre-signed URLs


export async function uploadToS3(file, key, concurrency, onStatusUpdate) {
  onStatusUpdate({ message: 'Preparing' });
  const numChunks = getNumChunks(file.size);

  const upload = await createUpload(key, file.type, numChunks);
  const chunks = getChunks(upload.urls, file.size);

  // This is where we actually upload the chunks
  const parts = await uploadChunks(chunks, file, concurrency, onStatusUpdate);

  onStatusUpdate({ message: 'Finalizing' });
  await completeUpload(key, upload.id, parts);

  onStatusUpdate({ message: 'Complete' });
}


export function getNumChunks(size) {
  const chunkSize = getChunkSize(size);

  const numChunks = Math.ceil(size / chunkSize);
  return numChunks;
}


async function createUpload(key, contentType, numParts) {
  const headers = getHeaders();

  const body = {
    key: key,
    content_type: contentType,
    number_of_parts: numParts,
  };

  const res = await fetch('/api/v1/multipart-s3-upload/create/', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const upload = await res.json();
  return upload;
}


async function completeUpload(key, id, parts) {
  const headers = getHeaders();

  const body = {
    key: key,
    upload_id: id,
    parts: parts,
  };

  const res = await fetch('/api/v1/multipart-s3-upload/complete/', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const result = await res.json();
  return result;
}


function getChunks(urls, fileSize) {
  const chunkSize = getChunkSize(fileSize);
  const chunks = [];

  for (let i = 0; i < urls.length; i++) {
    const url = urls[i];

    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, fileSize);

    const chunk = {
      url,
      start,
      end,
      n: i + 1,
    };
    chunks.push(chunk);
  }

  return chunks;
}


async function uploadOneChunk(chunk, file) {
  const response = await fetch(chunk.url, {
    method: 'PUT',
    body: file.slice(chunk.start, chunk.end),
  });

  // ETag is a hash of the chunk that S3 uses to verify the upload
  // In the S3 bucket CORS settings, we must have ETag in the list of exposed headers.
  const ETag = response.headers.get('ETag');

  const part = {
    PartNumber: chunk.n,
    ETag,
  };
  return part;
}


async function uploadOneChunkWithRetry(chunk, file, maxRetries=5) {
  let error = null;
  const baseDelay = 500; // ms

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await uploadOneChunk(chunk, file);
      //
    } catch (e) {
      error = e;
      console.error('Chunk upload failed', e);
      console.log('Retrying chunk', chunk.n);

      // Exponential backoff: baseDelay * 2, 4, 8, etc
      const delay = baseDelay * (2 ** i);

      // Add some randomness to avoid thundering herd 🐘🐘🐘
      const jitter = Math.random() * baseDelay;

      await sleep(delay + jitter);
    }
  }

  // If all retries fail, throw the last error.
  throw error;
}


function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}


// This allows multiple chunks to upload in parallel
// But also allows us to limit the number running at once (concurrency)
// Seems like 5 is a sensible default but can be adjusted if needed
async function uploadChunks(chunks, file, concurrency, onStatusUpdate) {
  const uploads = []; // This will be an array of promises (one per chunk)
  const active = new Set(); // Chunks that are actively uploading

  const status = {
    message: 'Uploading',
    started: new Date(),
    size: file.size,
    uploaded: 0,
    percentComplete: 0,
    chunks: { total: chunks.length, uploaded: 0 },
  };
  onStatusUpdate(status);

  for (const chunk of chunks) {
    // If the number of active uploads is over the limit, wait for some to complete.
    while (active.size >= concurrency) {
      await Promise.race(active);
    }

    // Now that we are under the limit, start the next upload.
    const upload = uploadOneChunkWithRetry(chunk, file, 5).then((part) => {
      // Remove the upload promise from the set once completed
      active.delete(upload);

      // Update the status
      status.uploaded += chunk.end - chunk.start;
      status.chunks.uploaded += 1;
      status.percentComplete = (status.uploaded / status.size) * 100;
      onStatusUpdate({ ...status });

      return part;
    });

    active.add(upload);
    uploads.push(upload);
  }

  // Wait for all uploads to complete
  return Promise.all(uploads);
}


function getChunkSize(size) {
  const defaultSize = Math.ceil(size / 100); // Use 100 chunks as a starting point

  const minSize = 5 * 1024 * 1024; // 5MB (Minimum allowed by S3)
  const maxSize = 100 * 1024 * 1024; // 100MB (This can go up to 5GB)

  if (defaultSize < minSize) {
    return minSize;
  }

  if (defaultSize > maxSize) {
    return maxSize;
  }

  return defaultSize;
}


function getHeaders() {
  return {
    'X-CSRFToken': getCSRFToken(),
    'Content-Type': 'application/json',
  };
}


function getCSRFToken() {
  const name = 'csrfmiddlewaretoken';
  const elem = document.querySelector(`[name="${name}"]`);
  return elem.value;
}
